import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiArrowLeft, FiDownload, FiEdit2, FiTrash2, FiFile, FiStar } from 'react-icons/fi';
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
  FaFileImage,
  FaFileArchive,
  FaFileAlt,
  FaStar
} from 'react-icons/fa';
import { Modal, Button } from 'react-bootstrap';
import FileViewer from 'react-file-viewer';
import axiosInstance from '../../services/AxiosInstance';
import { formatDateToDDMMMYYYY, formatDateTimeToDDMMMYYYY } from '../../utils/dateFormatter';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import { usePageToolbar } from '../layouts/nav/PageToolbarContext';
import '../tasks/taskdetails.css';
import '../Documents/commonstyle.css';

const StaffDocumentDetails = () => {
  const { staffId, documentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userdata, setUserdata] = useState(null);
  // Document preview state
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [classificationPath, setClassificationPath] = useState([]);
  const [useDirectUrl, setUseDirectUrl] = useState(false);

  const notifySuccess = (msg) => toast.success(`✔️ ${msg}`, {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: true,
    draggable: true,
  });

  const notifyError = (msg) => toast.error(`❌ ${msg}`, {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });

  const fetchDocumentDetails = useCallback(async () => {
    if (!documentId || !staffId) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get(`/staff/${staffId}/documents/${documentId}`);

      if (response && response.status === 200 && response.data.success) {
        const responseData = response.data.data;
        const document = responseData.Document || {};
        
        // Format document data based on the actual API response structure
        const formattedDocument = {
          id: responseData.id || document.id,
          document_id: responseData.document_id || document.id,
          name: document.file_name || responseData.Document?.file_name,
          file_name: document.file_name || responseData.Document?.file_name,
          file_size: document.file_size || responseData.Document?.file_size,
          size: document.file_size 
            ? `${(parseFloat(document.file_size) / 1024 / 1024).toFixed(2)} MB` 
            : '0 MB',
          file_type: document.file_type || responseData.Document?.file_type,
          // file_url is at the top level of responseData
          file_url: responseData.file_url || document.file_url || null,
          is_private: document.is_private || responseData.Document?.is_private || false,
          category_id: responseData.Category?.id || document.category_id || null,
          category_name: responseData.Category?.name || document.category_name || null,
          created_at: responseData.created_at || document.created_at,
          updated_at: responseData.updated_at || document.updated_at,
          created_by: document.uploaded_by || responseData.Document?.uploaded_by || null,
          uploaded_by: document.uploaded_by || responseData.Document?.uploaded_by || null,
          uploaded_by_user: document.uploaded_by_user || responseData.Document?.uploaded_by_user || null,
          created_by_name: document?.uploaded_by_user?.full_name || responseData?.Document?.uploaded_by_user?.full_name || null,
          description: document.description || responseData.Document?.description || null,
          branches: document.Branches || responseData.Document?.Branches || [],
          departments: document.Departments || responseData.Document?.Departments || [],
          sub_departments: document.SubDepartments || responseData.Document?.SubDepartments || [],
          // Document fields from responseData (staff document specific fields)
          document_title: responseData.document_title || document.document_title || null,
          registered_to: document.registered_to || responseData.Document?.registered_to || null,
          reference_number: document.reference_number || responseData.Document?.reference_number || null,
          issue_date: responseData.issued_date || document.issue_date || responseData.Document?.issue_date || null,
          expiry_date: responseData.expiry_date || document.expiry_date || responseData.Document?.expiry_date || null,
          remind_before: document.remind_before || responseData.Document?.remind_before || null,
          issuing_authority: responseData.issued_by || document.issuing_authority || responseData.Document?.issuing_authority || null,
          other_comments: document.other_comments || responseData.Document?.other_comments || null,
          is_favorite: document.is_favorite || responseData.Document?.is_favorite || false,
          verification_status: responseData.verification_status || null,
          remarks: responseData.remarks || null,
        };
        setDocumentData(formattedDocument);
      }
    } catch (error) {
      console.error('Error fetching document details:', error);
      notifyError(error.response?.data?.message || 'Failed to load document details');

      // If 403/404, navigate back after delay
      if (error.response && (error.response.status === 403 || error.response.status === 404)) {
        setTimeout(() => {
          navigate(`/staff/staffDetails/${staffId}`);
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  }, [staffId, documentId, navigate]);

  // Fetch classification path
  const fetchClassificationPath = useCallback(async (categoryId, fallbackCategoryName = null) => {
    setClassificationPath(null);

    if (!categoryId) {
      setClassificationPath([]);
      return;
    }

    try {
      const path = [];
      let current = categoryId;

      while (current) {
        const res = await axiosInstance.get(`/document-categories/${current}`);
        const category = res.data.data || res.data;

        if (!category) break;

        path.unshift({
          id: category.id,
          name: category.name
        });

        current = category.parent_id;
      }

      if (path.length > 0) {
        setClassificationPath(path);
        return;
      }

      if (fallbackCategoryName) {
        setClassificationPath([{ id: categoryId, name: fallbackCategoryName }]);
      } else {
        setClassificationPath([]);
      }
    } catch (err) {
      if (err.response?.status === 500) {
        notifyError(err.response?.data?.message || "You cannot access this category.");
        setClassificationPath("ACCESS_DENIED");
        return;
      }

      console.error("Error fetching classification path:", err);

      if (fallbackCategoryName) {
        setClassificationPath([{ id: categoryId, name: fallbackCategoryName }]);
      } else {
        setClassificationPath([]);
      }
    }
  }, []);

  // Fetch document details on mount
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUserdata(userData);
    fetchDocumentDetails();
  }, [staffId, documentId, fetchDocumentDetails]);

  useEffect(() => {
    if (documentData?.category_id) {
      fetchClassificationPath(documentData.category_id, documentData.category_name);
    } else {
      setClassificationPath([]);
    }
  }, [documentData?.category_id, documentData?.category_name, fetchClassificationPath]);

  // Fetch file as blob to avoid CORS issues, or use direct URL if available
  useEffect(() => {
    if (!documentData || !documentData.id) {
      setPreviewUrl(null);
      setPreviewError(null);
      setLoadingPreview(false);
      setUseDirectUrl(false);
      return;
    }

    const fetchFileForPreview = async () => {
      try {
        setPreviewError(null);
        setLoadingPreview(true);

        // Get the file URL - try file_url first, then construct from download endpoint
        let fileUrl = documentData.file_url;

        // If file_url is not available, use the download endpoint
        if (!fileUrl && documentData.id && staffId) {
          fileUrl = `/staff/${staffId}/documents/${documentData.id}/download`;
        }

        if (!fileUrl) {
          setPreviewError('File URL not available');
          setLoadingPreview(false);
          return;
        }

        // Check if file_url is a full URL that we can use directly
        const isFullUrl = fileUrl.startsWith('http://') || fileUrl.startsWith('https://');
        
        // For images and PDFs, try using the direct URL first if it's a full URL
        const fileType = documentData.file_type?.toLowerCase() || '';
        const isImage = fileType.startsWith('image/');
        const isPdf = fileType === 'application/pdf' || fileType === 'pdf';
        
        if (isFullUrl && (isImage || isPdf)) {
          console.log('Using direct URL for preview:', fileUrl);
          setPreviewUrl(fileUrl);
          setUseDirectUrl(true);
          setLoadingPreview(false);
          return;
        }

        // For other cases, fetch as blob
        let pathToFetch = fileUrl;
        
        // If file_url is a full URL, extract just the path part for axiosInstance
        if (isFullUrl) {
          try {
            const urlObj = new URL(fileUrl);
            // Extract the path from the full URL (e.g., /api/storage/staff-documents/...)
            pathToFetch = urlObj.pathname + urlObj.search;
          } catch (urlError) {
            console.error('Error parsing URL:', urlError);
            // Fallback: try to extract path manually
            const match = fileUrl.match(/\/api\/(.+)$/);
            if (match) {
              pathToFetch = `/api/${match[1]}`;
            } else {
              console.warn('Could not extract path from URL, using full URL');
            }
          }
        }

        // Ensure path starts with /
        if (!pathToFetch.startsWith('/') && !pathToFetch.startsWith('http')) {
          pathToFetch = `/${pathToFetch}`;
        }

        console.log('Fetching file for preview:', pathToFetch);

        // Fetch the file as blob using authenticated request to avoid CORS issues
        const response = await axiosInstance.get(pathToFetch, {
          responseType: 'blob',
          headers: {
            'Accept': 'application/octet-stream'
          }
        });

        if (response.status === 200) {
          // Get the content type from response headers
          const contentType = response.headers['content-type'] ||
            response.headers['Content-Type'] ||
            documentData.file_type ||
            'application/pdf';

          console.log('File fetched successfully:', {
            contentType,
            size: response.data.size,
            type: response.data.type
          });

          // Create blob URL with proper MIME type
          const blob = new Blob([response.data], { type: contentType });
          const blobUrl = window.URL.createObjectURL(blob);
          console.log('Blob URL created:', blobUrl);
          setPreviewUrl(blobUrl);
          setUseDirectUrl(false);
          setLoadingPreview(false);
        }
      } catch (error) {
        console.error('Error fetching file for preview:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          url: documentData?.file_url
        });
        
        // If blob fetch failed and we have a full URL, try using it directly as fallback
        if (documentData.file_url && (documentData.file_url.startsWith('http://') || documentData.file_url.startsWith('https://'))) {
          console.log('Blob fetch failed, trying direct URL:', documentData.file_url);
          setPreviewUrl(documentData.file_url);
          setUseDirectUrl(true);
          setLoadingPreview(false);
        } else {
          setPreviewError('Failed to load file for preview. Please try downloading the file.');
          setPreviewUrl(null);
          setLoadingPreview(false);
        }
      }
    };

    fetchFileForPreview();

    return () => {
      setPreviewUrl((currentUrl) => {
        if (currentUrl && currentUrl.startsWith('blob:')) {
          URL.revokeObjectURL(currentUrl);
        }
        return null;
      });
    };
  }, [documentData?.file_url, documentData?.id, documentData?.file_type, staffId]);

  // Handle file viewer errors
  const onError = (e) => {
    console.error('Error loading file preview:', e);
    try {
      setPreviewError('Failed to load file preview. The file type may not be supported. Please try downloading the file.');
      setPreviewUrl(null);
    } catch (error) {
      console.error('Error setting preview error state:', error);
    }
  };


  const handleDownload = async () => {
    if (!documentData || !documentData.id) {
      notifyError('Invalid document');
      return;
    }

    try {
      setDownloading(true);

      const response = await axiosInstance.get(`/staff/${staffId}/documents/${documentData.id}/download`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/octet-stream'
        }
      });

      if (response.status === 200) {
        let fileName = documentData.name || documentData.file_name || 'document';

        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = decodeURIComponent(fileNameMatch[1].replace(/['"]/g, ''));
          }
        }

        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        notifySuccess('File downloaded successfully');
      }
    } catch (error) {
      console.error('Error downloading file:', error);

      if (error.response && error.response.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorJson = JSON.parse(errorText);
          notifyError(errorJson.message || 'Failed to download file');
        } catch (parseError) {
          notifyError('Failed to download file');
        }
      } else {
        const errorMessage = error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to download file';
        notifyError(errorMessage);
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleAddFavorite = async () => {
    if (!documentData || !documentData.id) {
      notifyError('Invalid document');
      return;
    }

    try {
      setLoading(true);

      const response = await axiosInstance.put(`/staff/${staffId}/documents/${documentData.id}/favorite`);

      if (response.status === 200 || response.status === 204) {
        const isFavorited = response.data?.data?.is_favorite ?? true;
        
        setDocumentData(prev => ({
          ...prev,
          is_favorite: isFavorited
        }));
        
        if (isFavorited) {
          notifySuccess('Document added to favorites');
        } else {
          notifySuccess('Document removed from favorites');
        }
        await fetchDocumentDetails();
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to toggle favorite';
      notifyError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!documentData) return;

    try {
      setLoading(true);
      const response = await axiosInstance.delete(`/staff/${staffId}/documents/${documentData.id}`);

      if (response.status === 200 || response.status === 204) {
        const responseData = response.data;

        if (responseData && responseData.success === false) {
          notifyError(responseData.message || 'Failed to delete document');
        } else {
          notifySuccess('Document deleted successfully');
          setShowDeleteModal(false);
          setTimeout(() => {
            navigate(`/staff/staffDetails/${staffId}`);
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to delete document';
      notifyError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateBack = () => {
    if (location.state?.from) {
      navigate(-1);
    } else {
      navigate(`/staff/staffDetails/${staffId}`);
    }
  };

  // Helper function to convert MIME type to file extension
  const getFileExtensionFromMimeType = (fileType) => {
    if (!fileType) return '';
    
    let normalizedType = fileType.toLowerCase().trim();
    
    // If it's already an extension (no '/'), return it
    if (!normalizedType.includes('/')) {
      if (normalizedType[0] === '.') {
        normalizedType = normalizedType.substring(1);
      }
      return normalizedType;
    }
    
    // It's a MIME type, convert to extension
    const mimeToExt = {
      'application/pdf': 'pdf',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/vnd.ms-excel': 'xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
      'application/vnd.ms-powerpoint': 'ppt',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'text/plain': 'txt',
      'application/zip': 'zip',
      'application/x-rar-compressed': 'rar',
      'application/x-zip-compressed': 'zip'
    };
    
    return mimeToExt[normalizedType] || normalizedType.split('/').pop() || '';
  };

  const getFileIcon = (fileType, classificationName = null) => {
    if (!fileType) return 'file';

    // Convert MIME type to extension if needed
    const extension = getFileExtensionFromMimeType(fileType);

    const iconMap = {
      pdf: 'pdf',
      doc: 'word',
      docx: 'word',
      xls: 'excel',
      xlsx: 'excel',
      ppt: 'powerpoint',
      pptx: 'powerpoint',
      jpg: 'image',
      jpeg: 'image',
      png: 'image',
      gif: 'image',
      txt: 'text',
      zip: 'archive',
      rar: 'archive'
    };

    return iconMap[extension] || 'file';
  };

  const getFileIconComponent = (fileType, size = 64, classificationName = null) => {
    const iconType = getFileIcon(fileType, classificationName);

    switch (iconType) {
      case 'pdf':
        return <FaFilePdf size={size} style={{ color: '#d93025' }} />;
      case 'word':
        return <FaFileWord size={size} style={{ color: '#2b579a' }} />;
      case 'excel':
        return <FaFileExcel size={size} style={{ color: '#1d6f42' }} />;
      case 'powerpoint':
        return <FaFilePowerpoint size={size} style={{ color: '#d04423' }} />;
      case 'image':
        return <FaFileImage size={size} style={{ color: '#34a853' }} />;
      case 'archive':
        return <FaFileArchive size={size} style={{ color: '#ea4335' }} />;
      case 'text':
        return <FaFileAlt size={size} style={{ color: '#6c757d' }} />;
      default:
        return <FiFile size={size} style={{ color: '#667eea' }} />;
    }
  };

  const detailToolbar = useMemo(() => (
    <>
      <div className="page-toolbar-bar__start">
        <button
          type="button"
          className="page-toolbar-back-btn"
          onClick={handleNavigateBack}
          title="Back"
          aria-label="Back"
        >
          <FiArrowLeft />
          Back
        </button>
      </div>
      {documentData ? (
        <div className="page-toolbar-bar__end">
          <button
            className="header-toolbar-btn"
            onClick={() => navigate(`/staff/${staffId}/document/${documentId}/edit`)}
            type="button"
            title="Edit Document"
            aria-label="Edit Document"
          >
            <FiEdit2 />
          </button>
          <button
            className="header-toolbar-btn"
            onClick={handleAddFavorite}
            type="button"
            disabled={loading}
            title={loading ? (documentData?.is_favorite ? 'Removing...' : 'Adding...') : (documentData?.is_favorite ? 'Remove from Favorites' : 'Add to Favorites')}
            aria-label={documentData?.is_favorite ? 'Remove from Favorites' : 'Add to Favorites'}
            style={documentData?.is_favorite ? { color: '#ffc107' } : undefined}
          >
            {documentData?.is_favorite ? <FaStar /> : <FiStar />}
          </button>
          <button
            className="header-toolbar-btn"
            onClick={() => setShowDeleteModal(true)}
            type="button"
            style={{ color: '#dc3545' }}
            title="Delete"
            aria-label="Delete"
          >
            <FiTrash2 />
          </button>
        </div>
      ) : null}
    </>
  ), [documentData, loading, staffId, documentId, navigate, location.state?.from]);

  usePageToolbar(detailToolbar, [detailToolbar]);

  if (loading && !documentData) {
    return (
      <React.Fragment>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-3">
              <h5>Loading document details...</h5>
              <p className="text-muted">Please wait while we fetch the document information.</p>
            </div>
          </div>
        </div>
        <ToastContainer />
      </React.Fragment>
    );
  }

  if (!documentData) {
    return (
      <React.Fragment>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
          <div className="text-center">
            <h5>Document not found</h5>
            <p className="text-muted">The document you're looking for doesn't exist or you don't have permission to view it.</p>
            <button className="btn btn-primary mt-3" onClick={handleNavigateBack}>
              Go Back
            </button>
          </div>
        </div>
        <ToastContainer />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div className="row">
        <ToastContainer />
        <div className="col-xl-8 col-xxl-8" style={{ height: 'fit-content' }}>
          <div className="card heightcard">
            <div className="card-header details-card-header" style={{ position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', width: '100%' }}>
                <div className="document-icon-header">
                  {getFileIconComponent(documentData.file_type, 34, documentData.category_name)}
                </div>
                <h5 className="text-base sm:text-lg font-semibold" style={{ margin: 0, textAlign: 'right', flex: 1 }}>
                  {documentData.document_title || documentData.name || documentData.file_name || documentData.name}
                </h5>
              </div>
            </div>
            {classificationPath && Array.isArray(classificationPath) && classificationPath.length > 0 && (
              <div style={{
                padding: '0.75rem 0',
                borderTop: '1px solid #e9ecef'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  justifyContent: 'flex-end',
                  flexWrap: 'wrap'
                }}>
                  <span style={{
                    fontSize: '0.75rem',
                    color: '#6c757d',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Uploaded under:
                  </span>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap'
                  }}>
                    {classificationPath.map((category, index) => (
                      <span key={category.id} style={{ display: 'flex', alignItems: 'center' }}>
                        {index > 0 && (
                          <span style={{
                            margin: '0 0.5rem',
                            color: '#adb5bd',
                            fontSize: '0.875rem',
                            fontWeight: '300'
                          }}>
                            /
                          </span>
                        )}
                        <span style={{
                          color: index === classificationPath.length - 1 ? '#212529' : '#6c757d',
                          fontWeight: index === classificationPath.length - 1 ? '600' : '500',
                          fontSize: '0.875rem',
                          padding: '0.25rem 0.5rem',
                          backgroundColor: index === classificationPath.length - 1 ? '#ffffff' : 'transparent',
                          borderRadius: '4px',
                          transition: 'all 0.2s ease',
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                        onClick={() => navigate(`/documents/folder/${category.id}`)}
                        >
                          {category.name}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="card-body" style={{ paddingTop: '12px' }}>
              <section>
                <div className="row">
                  {(documentData.reference_number || documentData.expiry_date || documentData.registered_to || documentData.issue_date || documentData.issuing_authority || documentData.other_comments) && (
                    <div className="col-lg-12 mb-4">
                      <div className="row">
                        {documentData.reference_number && (
                          <div className="col-md-4 mb-3">
                            <label className="text-label" style={{
                              marginBottom: 0,
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: '#495057',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>Reference Number</label>
                            <div style={{
                              padding: '0.75rem 0',
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              fontSize: '1rem',
                              fontWeight: '500',
                              color: '#212529',
                              wordBreak: 'break-word',
                              transition: 'all 0.2s ease',
                              minHeight: '48px',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {documentData.reference_number}
                            </div>
                          </div>
                        )}

                        {documentData.expiry_date && (
                          <div className="col-md-4 mb-3">
                            <label className="text-label" style={{
                              marginBottom: 0,
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: '#495057',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>Expiry Date</label>
                            <div style={{
                              padding: '0.75rem 0',
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              fontSize: '1rem',
                              fontWeight: '500',
                              color: '#212529',
                              wordBreak: 'break-word',
                              transition: 'all 0.2s ease',
                              minHeight: '48px',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {formatDateToDDMMMYYYY(documentData.expiry_date)}
                            </div>
                          </div>
                        )}

                        {documentData.registered_to && (
                          <div className="col-md-4 mb-3">
                            <label className="text-label" style={{
                              marginBottom: 0,
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: '#495057',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>Registered To</label>
                            <div style={{
                              padding: '0.75rem 0',
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              fontSize: '1rem',
                              fontWeight: '500',
                              color: '#212529',
                              wordBreak: 'break-word',
                              transition: 'all 0.2s ease',
                              minHeight: '48px',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {documentData.registered_to}
                            </div>
                          </div>
                        )}

                        {documentData.issue_date && (
                          <div className="col-md-4 mb-3">
                            <label className="text-label" style={{
                              marginBottom: 0,
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: '#495057',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>Issue Date</label>
                            <div style={{
                              padding: '0.75rem 0',
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              fontSize: '1rem',
                              fontWeight: '500',
                              color: '#212529',
                              wordBreak: 'break-word',
                              transition: 'all 0.2s ease',
                              minHeight: '48px',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {formatDateToDDMMMYYYY(documentData.issue_date)}
                            </div>
                          </div>
                        )}

                        {documentData.issuing_authority && (
                          <div className="col-md-4 mb-3">
                            <label className="text-label" style={{
                              marginBottom: 0,
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: '#495057',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>Issuing Authority</label>
                            <div style={{
                              padding: '0.75rem 0',
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              fontSize: '1rem',
                              fontWeight: '500',
                              color: '#212529',
                              wordBreak: 'break-word',
                              transition: 'all 0.2s ease',
                              minHeight: '48px',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {documentData.issuing_authority}
                            </div>
                          </div>
                        )}

                        {documentData.other_comments && (
                          <div className="col-md-12 mb-3">
                            <label className="text-label" style={{
                              marginBottom: 0,
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: '#495057',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>Other Comments</label>
                            <div style={{
                              padding: '1rem 0',
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              fontSize: '1rem',
                              fontWeight: '400',
                              color: '#495057',
                              wordBreak: 'break-word',
                              whiteSpace: 'pre-wrap',
                              transition: 'all 0.2s ease',
                              lineHeight: '1.6',
                              minHeight: '80px'
                            }}>
                              {documentData.other_comments}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {documentData.description && (
                    <div className="col-lg-12 mb-4">
                      <label className="text-label">Description</label>
                      <div className="document-description p-3">
                        <p>{documentData.description}</p>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>

          {/* Document Preview Section */}
          {documentData && (documentData.file_url || documentData.id) && (
            <div className="card mt-4" style={{ border: '1px solid #e9ecef', borderRadius: '8px' }}>
              <div className="card-header" style={{
                background: '#f8f9fa',
                borderBottom: '1px solid #e9ecef',
                padding: '1rem'
              }}>
                <h6 className="mb-0 fw-semibold">Document Preview</h6>
              </div>
              <div className="card-body" style={{ padding: '1rem' }}>
                <div className="document-preview-container" style={{
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  padding: '1rem',
                  minHeight: '500px',
                  maxHeight: '600px',
                  backgroundColor: '#f8f9fa',
                  overflow: (() => {
                    // Extract file type, handling both MIME types and extensions
                    let fileType = '';
                    if (documentData?.file_type) {
                      const rawFileType = documentData.file_type;
                      if (rawFileType.includes('/')) {
                        // MIME type - extract extension
                        const mimeToExt = {
                          'image/jpeg': 'jpg',
                          'image/jpg': 'jpg',
                          'image/png': 'png',
                          'image/gif': 'gif',
                          'image/bmp': 'bmp',
                          'image/webp': 'webp'
                        };
                        const normalizedMime = rawFileType.toLowerCase().trim();
                        fileType = mimeToExt[normalizedMime] || normalizedMime.split('/').pop() || '';
                      } else {
                        fileType = rawFileType.replace(/^\./, '').toLowerCase();
                      }
                    } else {
                      fileType = (documentData?.name || documentData?.file_name || '').split('.').pop()?.toLowerCase() || '';
                    }
                    const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
                    return imageTypes.includes(fileType) ? 'hidden' : 'auto';
                  })(),
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {loadingPreview ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '500px' }}>
                      <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted">Loading preview...</p>
                      </div>
                    </div>
                  ) : previewError ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '500px' }}>
                      <div className="text-center">
                        <FiFile size={48} className="text-muted mb-3" />
                        <p className="text-muted">{previewError}</p>
                        <button
                          className="btn btn-primary mt-3"
                          onClick={handleDownload}
                          disabled={downloading}
                        >
                          <FiDownload className="me-2" />
                          {downloading ? 'Downloading...' : 'Download File'}
                        </button>
                      </div>
                    </div>
                  ) : previewUrl ? (
                    (() => {
                      // Extract file type, handling both MIME types (image/png) and extensions (.png)
                      let fileType = '';
                      if (documentData?.file_type) {
                        const rawFileType = documentData.file_type;
                        // Check if it's a MIME type (contains '/')
                        if (rawFileType.includes('/')) {
                          // Extract extension from MIME type
                          const mimeToExt = {
                            'application/pdf': 'pdf',
                            'application/msword': 'doc',
                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
                            'application/vnd.ms-excel': 'xls',
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
                            'application/vnd.ms-powerpoint': 'ppt',
                            'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
                            'image/jpeg': 'jpg',
                            'image/jpg': 'jpg',
                            'image/png': 'png',
                            'image/gif': 'gif',
                            'text/plain': 'txt'
                          };
                          const normalizedMime = rawFileType.toLowerCase().trim();
                          fileType = mimeToExt[normalizedMime] || normalizedMime.split('/').pop() || '';
                        } else {
                          // It's already an extension
                          fileType = rawFileType.replace(/^\./, '').toLowerCase();
                        }
                      } else {
                        // Fallback to file name extension
                        fileType = (documentData?.name || documentData?.file_name || '').split('.').pop()?.toLowerCase() || '';
                      }

                      if (fileType === 'xlsx' || fileType === 'xls') {
                        return (
                          <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '500px' }}>
                            <FaFileExcel size={64} className="text-muted mb-3" style={{ color: '#1d6f42' }} />
                            <p className="text-muted mb-3">Excel files cannot be previewed in the browser.</p>
                            <button
                              className="btn btn-primary"
                              onClick={handleDownload}
                              disabled={downloading}
                            >
                              <FiDownload className="me-2" />
                              {downloading ? 'Downloading...' : 'Download File'}
                            </button>
                          </div>
                        );
                      }

                      if (fileType === 'pdf') {
                        return (
                          <iframe
                            src={`${previewUrl}#toolbar=0`}
                            style={{
                              width: '100%',
                              height: '100%',
                              minHeight: '500px',
                              border: 'none',
                              flex: 1
                            }}
                            title="PDF Preview"
                            onError={(e) => {
                              console.error('Iframe error:', e);
                              onError(e);
                            }}
                          />
                        );
                      }

                      // Extract file type for image check
                      let imageFileType = fileType;
                      if (documentData?.file_type && documentData.file_type.includes('/')) {
                        const mimeToExt = {
                          'image/jpeg': 'jpg',
                          'image/jpg': 'jpg',
                          'image/png': 'png',
                          'image/gif': 'gif',
                          'image/bmp': 'bmp',
                          'image/webp': 'webp'
                        };
                        const normalizedMime = documentData.file_type.toLowerCase().trim();
                        imageFileType = mimeToExt[normalizedMime] || normalizedMime.split('/').pop() || fileType;
                      }
                      
                      const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
                      if (imageTypes.includes(imageFileType) && previewUrl) {
                        return (
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              minHeight: '500px',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              overflow: 'hidden'
                            }}
                          >
                            <img
                              src={previewUrl}
                              alt={documentData.name || documentData.file_name || 'document'}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain',
                                borderRadius: '4px',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
                              }}
                              onError={(e) => {
                                console.error('Image load error:', e);
                                setPreviewError('Failed to load image preview. Please try downloading the file.');
                                setPreviewUrl(null);
                              }}
                              onLoad={() => {
                                console.log('Image loaded successfully');
                                setLoadingPreview(false);
                              }}
                            />
                          </div>
                        );
                      }

                      const supportedPreviewTypes = ['doc', 'docx', 'ppt', 'pptx', 'txt', 'csv'];

                      if (!supportedPreviewTypes.includes(fileType)) {
                        return (
                          <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '500px' }}>
                            <FiFile size={64} className="text-muted mb-3" />
                            <p className="text-muted mb-3">Preview not available for this file type.</p>
                            <button
                              className="btn btn-primary"
                              onClick={handleDownload}
                              disabled={downloading}
                            >
                              <FiDownload className="me-2" />
                              {downloading ? 'Downloading...' : 'Download File'}
                            </button>
                          </div>
                        );
                      }

                      return (
                        <FileViewer
                          fileType={fileType}
                          filePath={previewUrl}
                          onError={onError}
                          errorComponent={<div className="text-center p-4"><p className="text-muted">Unable to preview this file type</p></div>}
                          unsupportedComponent={<div className="text-center p-4"><p className="text-muted">File type not supported for preview</p></div>}
                        />
                      );
                    })()
                  ) : (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '500px' }}>
                      <div className="text-center">
                        <FiFile size={48} className="text-muted mb-3" />
                        <p className="text-muted">Preview not available</p>
                        <button
                          className="btn btn-primary mt-3"
                          onClick={handleDownload}
                          disabled={downloading}
                        >
                          <FiDownload className="me-2" />
                          {downloading ? 'Downloading...' : 'Download File'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="col-xl-4 col-xxl-4">
          <div>
            <div className="status-box">
              Document Details
            </div>
            <div className="flex flex-col p-4 w-80 bg-white shadow-lg border-l document-sidebar">
              <div className="mb-3 sidebar">
                <div className="d-flex">
                  <div className="col-5">
                    <p className="text-sm text-gray-600 font-semibold alignments" style={{ margin: 0 }}>File Name:</p>
                  </div>
                  <div className="col-7">
                    <p className="text-gray-700" style={{
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      wordWrap: 'break-word',
                      margin: 0,
                      lineHeight: '1.5'
                    }}>{documentData.name || documentData.file_name}</p>
                  </div>
                </div>
              </div>

              <div className="mb-3 sidebar">
                <div className="d-flex">
                  <div className="col-5">
                    <p className="text-sm text-gray-600 font-semibold alignments" style={{ margin: 0 }}>File Size:</p>
                  </div>
                  <div className="col-7">
                    <p className="text-gray-700" style={{ margin: 0, lineHeight: '1.5' }}>{documentData.size}</p>
                  </div>
                </div>
              </div>

              {/* <div className="mb-3 sidebar">
                <div className="d-flex">
                  <div className="col-5">
                    <p className="text-sm text-gray-600 font-semibold alignments" style={{ margin: 0 }}>File Type:</p>
                  </div>
                  <div className="col-7">
                    <p className="text-gray-700" style={{ margin: 0, lineHeight: '1.5' }}>{documentData.file_type?.toUpperCase() || 'Unknown'}</p>
                  </div>
                </div>
              </div> */}

              <div className="mb-3 sidebar">
                <div className="d-flex">
                  <div className="col-5">
                    <p className="text-sm text-gray-600 font-semibold alignments" style={{ margin: 0 }}>Created on:</p>
                  </div>
                  <div className="col-7">
                    <p className="text-gray-700" style={{ margin: 0, lineHeight: '1.5' }}>{formatDateTimeToDDMMMYYYY(documentData.created_at)}</p>
                  </div>
                </div>
              </div>

              <div className="mb-3 sidebar">
                <div className="d-flex">
                  <div className="col-5">
                    <p className="text-sm text-gray-600 font-semibold alignments" style={{ margin: 0 }}>Last Modified:</p>
                  </div>
                  <div className="col-7">
                    <p className="text-gray-700" style={{ margin: 0, lineHeight: '1.5' }}>{formatDateTimeToDDMMMYYYY(documentData.updated_at || documentData.created_at)}</p>
                  </div>
                </div>
              </div>

              <div className="mb-3 sidebar">
                <div className="d-flex">
                  <div className="col-5">
                    <p className="text-sm text-gray-600 font-semibold alignments" style={{ margin: 0 }}>Privacy:</p>
                  </div>
                  <div className="col-7">
                    <p className="text-gray-700" style={{ margin: 0, lineHeight: '1.5' }}>{documentData.is_private ? 'Private' : 'Public'}</p>
                  </div>
                </div>
              </div>

              {documentData.created_by_name && (
                <div className="mb-3 sidebar">
                  <div className="d-flex">
                    <div className="col-5">
                      <p className="text-sm text-gray-600 font-semibold alignments" style={{ margin: 0 }}>Uploaded by:</p>
                    </div>
                    <div className="col-7">
                      <p className="text-gray-700" style={{
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        margin: 0,
                        lineHeight: '1.5'
                      }}>{documentData.created_by_name}</p>
                    </div>
                  </div>
                </div>
              )}

              {documentData.remind_before && (
                <div className="mb-3 sidebar">
                  <div className="d-flex">
                    <div className="col-5">
                      <p className="text-sm text-gray-600 font-semibold alignments" style={{ margin: 0 }}>Remind Before:</p>
                    </div>
                    <div className="col-7">
                      <p className="text-gray-700" style={{ margin: 0, lineHeight: '1.5' }}>{documentData.remind_before}</p>
                    </div>
                  </div>
                </div>
              )}

              {documentData.branches && documentData.branches.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 font-semibold">Branches:</p>
                  <div className="user-chip-list">
                    {documentData.branches.map((branch, index) => (
                      <div className="user-chip" key={index}>
                        <span className="user-chip-avatar">
                          <i className="fa fa-sitemap"></i>
                        </span>
                        {branch.name || branch.branch_name || 'Unknown Branch'}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {documentData.departments && documentData.departments.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 font-semibold">Departments:</p>
                  <div className="user-chip-list">
                    {documentData.departments.map((dept, index) => (
                      <div className="user-chip" key={index}>
                        <span className="user-chip-avatar">
                          <i className="fa fa-building"></i>
                        </span>
                        {dept.name || dept.department_name || 'Unknown Department'}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {documentData.sub_departments && documentData.sub_departments.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 font-semibold">Sub Departments:</p>
                  <div className="user-chip-list">
                    {documentData.sub_departments.map((subDept, index) => (
                      <div className="user-chip" key={index}>
                        <span className="user-chip-avatar">
                          <i className="fa fa-building"></i>
                        </span>
                        {subDept.name || subDept.sub_department_name || 'Unknown Sub Department'}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <InternalFooter />
    </React.Fragment>
  );
};

export default StaffDocumentDetails;